import { useEffect, useState } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { GraduationCap, CheckCircle2, X } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { useLang } from "@/context/LanguageContext";

const empty = { name: "", whatsapp: "", faculty: "", year: "", duration_prague: "", notes: "" };

export function StudentSignup() {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [texts, setTexts] = useState({});
  const [questions, setQuestions] = useState([]);

  useEffect(() => {
    api.get("/student-form").then((r) => {
      setTexts(r.data.texts || {});
      setQuestions(r.data.questions || []);
    }).catch(() => {});
  }, []);

  // text override → falls back to i18n default
  const tx = (key, i18nKey) => (texts[key] && String(texts[key]).trim()) || t(i18nKey);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setAnswer = (label, v) => setAnswers((a) => ({ ...a, [label]: v }));
  const toggleMulti = (label, opt) =>
    setAnswers((a) => {
      const cur = Array.isArray(a[label]) ? a[label] : [];
      return { ...a, [label]: cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt] };
    });

  const validateQuestions = () => {
    for (const q of questions) {
      if (!q.required) continue;
      const v = answers[q.label];
      if (q.type === "checkbox") {
        if (!v) { toast.error(`${q.label} — ${t("rsvp.error")}`); return false; }
      } else if (q.type === "multiselect") {
        if (!Array.isArray(v) || v.length === 0) { toast.error(`${q.label} — ${t("rsvp.error")}`); return false; }
      } else if (v === undefined || v === null || String(v).trim() === "") {
        toast.error(`${q.label} — ${t("rsvp.error")}`); return false;
      }
    }
    return true;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.whatsapp.trim()) {
      toast.error(t("student.required"));
      return;
    }
    if (!validateQuestions()) return;
    setSubmitting(true);
    try {
      await api.post("/student-signups", { ...form, answers, lang });
      setDone(true);
      toast.success(t("student.toast"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    setOpen(false);
    setTimeout(() => { setDone(false); setForm(empty); setAnswers({}); }, 250);
  };

  const renderQuestion = (q) => {
    const val = answers[q.label];
    const star = q.required ? <span className="text-[#BE923C]"> *</span> : null;
    if (q.type === "textarea") {
      return (
        <div key={q.id}>
          <Label>{q.label}{star}</Label>
          <Textarea data-testid={`student-q-${q.id}`} value={val || ""} rows={3}
            onChange={(e) => setAnswer(q.label, e.target.value)} className="mt-1.5" />
        </div>
      );
    }
    if (q.type === "select") {
      return (
        <div key={q.id}>
          <Label>{q.label}{star}</Label>
          <select data-testid={`student-q-${q.id}`} value={val || ""}
            onChange={(e) => setAnswer(q.label, e.target.value)}
            className="mt-1.5 w-full rounded-md border border-[#0F172A]/10 bg-white px-3 py-2 text-sm">
            <option value="">{t("rsvp.select_ph")}</option>
            {(q.options || []).map((o, i) => <option key={i} value={o}>{o}</option>)}
          </select>
        </div>
      );
    }
    if (q.type === "multiselect") {
      const cur = Array.isArray(val) ? val : [];
      return (
        <div key={q.id} data-testid={`student-q-${q.id}`}>
          <Label>{q.label}{star}</Label>
          <div className="mt-2 space-y-2">
            {(q.options || []).map((o, i) => (
              <label key={i} className="flex items-start gap-2.5 text-sm text-[#0F172A] cursor-pointer rounded-lg border border-[#0F172A]/10 px-3 py-2 hover:border-[#BE923C]/50 transition-colors">
                <input type="checkbox" checked={cur.includes(o)} onChange={() => toggleMulti(q.label, o)}
                  data-testid={`student-q-${q.id}-opt-${i}`} className="mt-0.5 w-4 h-4 accent-[#BE923C]" />
                <span>{o}</span>
              </label>
            ))}
          </div>
        </div>
      );
    }
    if (q.type === "checkbox") {
      return (
        <label key={q.id} className="flex items-start gap-3 text-sm text-[#0F172A] cursor-pointer">
          <input type="checkbox" data-testid={`student-q-${q.id}`} checked={!!val}
            onChange={(e) => setAnswer(q.label, e.target.checked)} className="mt-0.5 w-4 h-4 accent-[#BE923C]" />
          <span>{q.label}{star}</span>
        </label>
      );
    }
    return (
      <div key={q.id}>
        <Label>{q.label}{star}</Label>
        <Input data-testid={`student-q-${q.id}`} value={val || ""}
          onChange={(e) => setAnswer(q.label, e.target.value)} className="mt-1.5" />
      </div>
    );
  };

  return (
    <section className="relative z-10 py-14 sm:py-16" data-testid="student-signup-section">
      <div className="mx-auto max-w-4xl px-5 sm:px-8">
        <div className="relative overflow-hidden rounded-3xl bg-[#0F172A] text-white p-8 sm:p-12 shadow-[0_20px_60px_rgba(15,23,42,0.18)]">
          <div className="pointer-events-none absolute -top-24 -right-16 h-64 w-64 rounded-full bg-[#BE923C]/25 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 -left-16 h-64 w-64 rounded-full bg-[#BE923C]/15 blur-3xl" />
          <div className="relative flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-8">
            <div className="flex-1">
              <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-[#E7C77E]">
                <GraduationCap size={14} /> {tx("signup_title", "student.signup_title")}
              </span>
              <p className="mt-4 text-lg sm:text-xl leading-relaxed text-white/85">{tx("signup_desc", "student.signup_desc")}</p>
            </div>
            <button
              data-testid="student-signup-open"
              onClick={() => setOpen(true)}
              className="shrink-0 rounded-full bg-gradient-to-r from-[#E7C77E] to-[#BE923C] px-8 py-4 text-sm font-bold text-[#0F172A] shadow-[0_10px_30px_rgba(190,146,60,0.4)] hover:shadow-[0_14px_40px_rgba(190,146,60,0.55)] hover:-translate-y-0.5 transition-all"
            >
              {tx("signup_btn", "student.signup_btn")}
            </button>
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : close())}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="student-signup-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{tx("form_title", "student.form_title")}</DialogTitle>
          </DialogHeader>

          <AnimatePresence mode="wait">
            {done ? (
              <motion.div
                key="done"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex flex-col items-center text-center py-6"
                data-testid="student-signup-success"
              >
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#EEF4EC] text-[#4b7a43]">
                  <CheckCircle2 size={30} />
                </div>
                <p className="mt-4 text-lg font-semibold text-[#0F172A]">{tx("success", "student.success")}</p>
                <button
                  onClick={close}
                  data-testid="student-signup-close"
                  className="mt-6 rounded-full bg-[#0F172A] text-white text-sm font-semibold px-6 py-3 hover:bg-[#1e293b] transition-colors"
                >
                  <X size={15} className="inline mr-1.5 -mt-0.5" /> {t("program.back")}
                </button>
              </motion.div>
            ) : (
              <motion.form
                key="form"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                onSubmit={submit}
                className="space-y-4 pt-1"
              >
                <p className="text-sm text-[#475569]">{tx("form_intro", "student.form_intro")}</p>

                <div>
                  <Label htmlFor="st-name">{tx("name_label", "student.name")}</Label>
                  <Input id="st-name" data-testid="student-field-name" value={form.name}
                    onChange={(e) => set("name", e.target.value)} placeholder={t("student.name_ph")} className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="st-whatsapp">{tx("whatsapp_label", "student.whatsapp")}</Label>
                  <Input id="st-whatsapp" data-testid="student-field-whatsapp" value={form.whatsapp}
                    onChange={(e) => set("whatsapp", e.target.value)} placeholder={t("student.whatsapp_ph")} className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="st-faculty">{tx("faculty_label", "student.faculty")}</Label>
                  <Input id="st-faculty" data-testid="student-field-faculty" value={form.faculty}
                    onChange={(e) => set("faculty", e.target.value)} placeholder={t("student.faculty_ph")} className="mt-1.5" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="st-year">{tx("year_label", "student.year")}</Label>
                    <Input id="st-year" data-testid="student-field-year" value={form.year}
                      onChange={(e) => set("year", e.target.value)} placeholder={t("student.year_ph")} className="mt-1.5" />
                  </div>
                  <div>
                    <Label htmlFor="st-duration">{tx("duration_label", "student.duration")}</Label>
                    <Input id="st-duration" data-testid="student-field-duration" value={form.duration_prague}
                      onChange={(e) => set("duration_prague", e.target.value)} placeholder={t("student.duration_ph")} className="mt-1.5" />
                  </div>
                </div>

                {questions.map(renderQuestion)}

                <div>
                  <Label htmlFor="st-notes">{tx("notes_label", "student.notes")}</Label>
                  <Textarea id="st-notes" data-testid="student-field-notes" value={form.notes}
                    onChange={(e) => set("notes", e.target.value)} placeholder={t("student.notes_ph")} rows={3} className="mt-1.5" />
                </div>

                <button
                  type="submit"
                  data-testid="student-signup-submit"
                  disabled={submitting}
                  className="w-full rounded-full bg-gradient-to-r from-[#E7C77E] to-[#BE923C] px-8 py-4 text-sm font-bold text-[#0F172A] shadow-[0_10px_30px_rgba(190,146,60,0.35)] hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:translate-y-0"
                >
                  {submitting ? t("student.sending") : t("student.submit")}
                </button>
              </motion.form>
            )}
          </AnimatePresence>
        </DialogContent>
      </Dialog>
    </section>
  );
}
